import React, { useState, useEffect, useCallback, Fragment } from 'react';

import { Form, Col, Button } from 'react-bootstrap';
import { isEmpty, map, find, upperFirst } from 'lodash';

import {
  ONE_OFF_SUBSCRIPTION_FREQUENCY,
  ToggleOneOffSubscriptionPayload,
} from 'services/basket.api';
import { isLoggedIn } from 'services/auth.api';
import ButtonLoader from '../common/ButtonLoader';
import { Switch, SwitchState } from '../common/Switch';
import Carousel from './Carousel';
import { PurchaseType } from '../../types';
import {
  BasketLine,
  Product,
  ProductInfo,
  ProductAttributeValues,
  PetList,
  HomePageOfferData,
} from '../../services/models';
import {
  calculateDiscount,
  getVoucherInBasket,
  getProductBoxesCount,
  constructProductUrl,
  getProductImageUrl,
  fetchIdFromUrl,
  updateAllPetsList,
  trackAddOrRemoveItem,
  getProductInfoFromId,
  getImageSource,
  isProductOfAttributeValue,
} from '../../utils';
import {
  removeLine,
  useAppDispatch,
  useAppSelector,
  toggleOneOffSubscription,
} from 'state';
import product_placeholder from '../../assets/product_placeholder.png';

interface Props {
  shippingCharge: string;
  ruralDelivery: string;
  dogsName: string;
  loading?: boolean;
  showPayNow?: boolean;
  changeQuantity?: Function;
  setPageLoading: Function;
  addProduct: Function;
  isTester?: boolean;
  boxCount?: number;
  productDetails: Product;
  dogsList: PetList[];
  allProducts: Product[];
  homePageOffer: HomePageOfferData;
  frequency: number;
  showLogin: boolean;
  errors: any;
  touched: any;
}

interface PetDataObject {
  petId: { petId: number; basketLines: BasketLine[]; dogDetails: PetList };
}

const OrderSummary: React.FC<Props> = props => {
  const {
    loading,
    isTester,
    addProduct,
    changeQuantity,
    shippingCharge,
    productDetails: mainProduct = {} as Product,
    showPayNow = true,
    dogsList = [],
    allProducts,
    setPageLoading,
    frequency,
    showLogin,
    ruralDelivery,
    errors,
    touched, //to handle showing error on bottom
  } = props;

  const dispatch = useAppDispatch();
  const basket = useAppSelector(state => state.basket.basket);
  const lines = useAppSelector(state => state.basket.lines);

  const [discount, setDiscount] = useState(0);
  const [error, setError] = useState([]);
  const [voucherApplied, setVoucherApplied] = useState(
    getVoucherInBasket(basket),
  );
  const [productBoxesCount, setProductBoxesCount] = useState(0);
  const [petData, setPetData] = useState({} as PetDataObject);
  const [oneOff, setOneOff] = useState(false);
  const [calculatedShippingCharge, setCalculatedShippingCharge] = useState(0);
  const [oneOffFrequency, setOneOffFrequency] = useState(
    basket?.frequency ? basket?.frequency : ONE_OFF_SUBSCRIPTION_FREQUENCY,
  );
  const [showOneOffFrequencyPicker, setShowOneOffFrequencyPicker] = useState(
    basket?.frequency ? true : false,
  );

  const publicAddOns = allProducts.filter(
    item =>
      item.is_public &&
      (isProductOfAttributeValue(item, ProductAttributeValues.ADD_ON) ||
        isProductOfAttributeValue(item, ProductAttributeValues.ONE_OFF)) &&
      item.is_suggestible,
  );

  const suggestibleProducts = allProducts.filter(
    item =>
      item.is_public &&
      (isProductOfAttributeValue(item, ProductAttributeValues.ADD_ON) ||
        isProductOfAttributeValue(item, ProductAttributeValues.ONE_OFF)) &&
      item.is_suggestible,
    // isProductOfAttributeValue(item, ProductAttributeValues.ONE_OFF),
  );

  const constructPetData = useCallback(
    (basketLines: BasketLine[]) => {
      let result = {} as PetDataObject;
      basketLines.forEach(lineItem => {
        if (lineItem.pet) {
          const petId = fetchIdFromUrl(lineItem.pet);
          const existing = result[petId];
          result[petId] = {
            petId,
            basketLines: existing?.basketLines?.length
              ? existing.basketLines.concat(lineItem)
              : [lineItem],
            dogDetails: find(dogsList, item => item.petDetails.id === petId),
          };
        }
      });
      setPetData(result);
    },
    [dogsList],
  );

  useEffect(() => {
    if (basket) {
      setDiscount(calculateDiscount(basket));
    }
    setVoucherApplied(getVoucherInBasket(basket));
    setPageLoading(false);
    constructPetData(lines);
  }, [lines, basket]);
  useEffect(() => {
    if (errors && touched && Object.keys(errors).length > 0) {
      const data = Object.keys(errors).map((field, index) => {
        return touched[field] ? errors[field] : '';
      }).filter(it=>it);
      setError(data);
    } else {
      setError([]);
    }
  }, [errors,touched]);
  useEffect(() => {
    setOneOffFrequency(
      basket?.frequency ? basket?.frequency : ONE_OFF_SUBSCRIPTION_FREQUENCY,
    );
    if (basket?.frequency) {
      setShowOneOffFrequencyPicker(true);
    }
  }, [basket]);

  useEffect(() => {
    if (Number(shippingCharge) != 0) {
      setCalculatedShippingCharge(Number(shippingCharge));
    } else {
      setCalculatedShippingCharge(Number(basket?.base_shipping_charge));
    }
  }, [basket, shippingCharge]);

  useEffect(() => {
    if (Object.keys(petData).length === 0) {
      setOneOff(true);
    } else {
      setOneOff(false);
    }
  }, [petData]);

  useEffect(() => {
    if (lines && lines.length) {
      setProductBoxesCount(lines.filter(it => it?.add_on === false).length);
    }
  }, [lines, mainProduct]);

  const addAddon = async (item: ProductInfo, petId: number) => {
    setPageLoading(true);

    await addProduct(
      item.url,
      petId,
      oneOff ? PurchaseType.ONE_OFF : PurchaseType.ADD_ON,
    );
    trackAddOrRemoveItem(item, 'add');
    setPageLoading(false);
  };

  const removeLineItem = async (basketLine: BasketLine) => {
    setPageLoading(true);

    // Remove all basketlines of the pet if chicken dog box is removed
    if (basketLine.type_of_order_basket === 'first') {
      const petIdForDeleted = fetchIdFromUrl(basketLine.pet as string);
      const newPetList = [...dogsList];

      const petIndex = newPetList.findIndex(
        item => item.petDetails.id === petIdForDeleted,
      );
      newPetList.splice(petIndex, 1);

      const updatedPetData = { ...petData };
      await Promise.all(
        updatedPetData[petIdForDeleted].basketLines.map(async item => {
          await dispatch(removeLine(item.id));
        }),
      );

      delete updatedPetData[petIdForDeleted];

      setPetData(updatedPetData);
      updateAllPetsList(newPetList);
    } else {
      await dispatch(removeLine(basketLine.id));
    }
    const productId = fetchIdFromUrl(basketLine.product);
    const productData = getProductInfoFromId(allProducts, productId) as Product;
    trackAddOrRemoveItem(productData, 'remove');
    setPageLoading(false);
  };

  const handleQuantityChange = async (basketId, basketLine, quantity) => {
    setPageLoading(true);
    await changeQuantity(basketId, basketLine, quantity);
    const productId = fetchIdFromUrl(basketLine.product);
    const productData = getProductInfoFromId(allProducts, productId) as Product;
    if (quantity > 0) {
      trackAddOrRemoveItem(productData, 'add');
    } else if (quantity < 0) {
      trackAddOrRemoveItem(productData, 'remove');
    }
    setPageLoading(false);
  };

  const getTotal = () => {
    return (
      Math.round(
        (Number(basket?.total_incl_tax) + calculatedShippingCharge) * 100,
      ) / 100
    );
  };

  const getProductDisplayName = (isTreat: boolean, basketLine: BasketLine) => {
    const productData = find(allProducts, { url: basketLine.product });
    const itemLimit: number = productData?.items_in_cart_limit || 4;
    if (isTreat) {
      return (
        <div className="product-display">
          <div className="product-data">
            <span>{productData?.title}</span>
            <span style={{ fontWeight: 600 }}>
              ($
              {basket?.subscription
                ? productData?.subscription_price
                : productData?.price_incl_tax}
              )
            </span>
          </div>
          <div className="counter-button">
            <Button
              className="btn-decrement"
              type="button"
              disabled={loading}
              //text="-"
              //loadingText="Removing..."
              //loading={false}
              onClick={() => handleQuantityChange(basket.id, basketLine, -1)}
            >
              <b>-</b>
            </Button>
            <span className="counter">{basketLine.quantity}</span>
            <Button
              variant="secondary"
              size="sm"
              className="btn-increment"
              type="button"
              //text="+"
              //loadingText="Adding..."
              //loading={false}
              disabled={loading}
              onClick={() => {
                if (basketLine.quantity < itemLimit) {
                  handleQuantityChange(basket.id, basketLine, 1);
                } else {
                  alert(
                    "Sorry pet parents! You've reached maximum limit for treats allowed for each pet!",
                  );
                }
              }}
            >
              <b>+</b>
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div>
        {`${basketLine.quantity} x ${productData?.title} `} <br />
        {!isTester && !oneOff && `Every ${basketLine.frequency} days`}
        {productBoxesCount > 1 && (
          <>
            <br />
            <Button
              className="btn-txt btn-txt-remove-item"
              onClick={() => removeLineItem(basketLine)}
              disabled={loading}
            >
              Remove
            </Button>
          </>
        )}
      </div>
    );
  };

  const getSummaryLineItem = (isTreat: boolean, basketLine: BasketLine) => {
    const productData = find(allProducts, { url: basketLine.product });
    const productImage = getImageSource(productData);
    return (
      <div className="row adl-item">
        <div className="col-3">
          <div className="img-wrap">
            <img
              alt=""
              src={
                productImage
                  ? getProductImageUrl(productImage)
                  : product_placeholder
              }
            />
          </div>
        </div>
        <div className="col-7">
          <div className="adl-item-txt">
            <div className="adl-desc">
              {getProductDisplayName(isTreat, basketLine)}
            </div>
          </div>
        </div>
        <div
          className="col-2"
          style={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <div className="cost-wrap">
            ${basketLine.price_incl_tax_excl_discounts}
          </div>
        </div>
      </div>
    );
  };

  const formSummaryLineItems = (
    basketLines: BasketLine[],
    editable: boolean,
  ) => {
    return basketLines.map(basketLine => {
      if (
        oneOff ||
        (!oneOff &&
          ((editable && basketLine.add_on === true) ||
            (!editable && basketLine.add_on === false)))
      ) {
        if (basketLine.quantity > 0) {
          return (
            <div className="box-adl-items" key={basketLine.id}>
              {getSummaryLineItem(editable, basketLine)}
            </div>
          );
        }
      }
    });
  };

  const formSuggestibleProductsEl = (
    items: Product[],
    basketLines: BasketLine[],
    title: string,
    editable: boolean,
    petId: number | null,
    showSubscriptionAmount: boolean = false,
  ) => {
    if (isTester) {
      return null;
    }
    return (
      <>
        {!isEmpty(items) && (oneOff || (!oneOff && !isEmpty(basketLines))) && (
          <Fragment>
            <div className="box-adl-items">
              <div className="row adl-item">
                <div className="col-12">
                  <div className="adl-item-txt">
                    <div className="adl-title">
                      <span className="left">{title}</span>
                      {oneOff && <span className="right toggle-icon" />}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* <Carousel
              items={items.sort((item1, item2) => {
                if (item1.weightage > item2.weightage) return 1;
                else if (item1.weightage < item2.weightage) return -1;
                else return 0;
              })}
              onAddItem={addAddon}
              basketLines={basketLines}
              petId={petId}
              loading={loading}
              showSubscriptionAmount={showSubscriptionAmount}
            /> */}
          </Fragment>
        )}
        {formSummaryLineItems(basketLines, editable)}
      </>
    );
  };

  let allPetNames = '';
  dogsList?.map(item => {
    const name = item.petDetails.name;
    allPetNames = allPetNames
      ? `${allPetNames}, ${upperFirst(name)}`
      : upperFirst(name);
  });

  return (
    <div className="checkout-box" style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <div className="title mgn-bot-20">Order Summary</div>

        {!oneOff &&
          map(petData, (petOrderItem, index) => {
            return (
              <>
                <hr className="col-hide-md" />
                <div className="title mgn-bot-20">
                  {petOrderItem?.dogDetails?.petDetails.name}
                </div>

                {formSummaryLineItems(petOrderItem.basketLines, false)}

                {
                  petOrderItem.basketLines.length > 1 ? formSuggestibleProductsEl(
                  publicAddOns, 
                  petOrderItem.basketLines, 
                  'Add-Ons', 
                  true, 
                  petOrderItem.petId, 
                  true) : null
                }
              </>
            );
          })}

        {oneOff && (
          <>
            <hr className="col-hide-md" />

            {formSuggestibleProductsEl(
              suggestibleProducts,
              lines,
              'Sides',
              true,
              null,
              false,
            )}

            {!frequency &&
              oneOff &&
              basket &&
              lines.length > 0 &&
              process.env.SHOW_ONEOFF_SUBSCRIPTION == 'true' && (
                <SubscriptionSelection
                  basketId={basket.id}
                  frequency={oneOffFrequency}
                  setFrequency={setOneOffFrequency}
                  showFrequencyPicker={showOneOffFrequencyPicker}
                  setShowFrequencyPicker={setShowOneOffFrequencyPicker}
                />
              )}
          </>
        )}

        <hr className="col-hide-md" />
        <div className="title mgn-bot-20">Total due today</div>
        <div className="cost-bk-wrapper">
          <div className="cost-item">
            <div className="lt">Subtotal</div>
            <div className="rt">${basket?.total_incl_tax_excl_discounts}</div>
          </div>

          {voucherApplied && (
            <div className="cost-item">
              <div className="lt">Voucher</div>
              <div className="rt">{voucherApplied.code}</div>
            </div>
          )}

          {props.homePageOffer?.second_order ? (
            <div className="notification">
              {props.homePageOffer?.second_order_benefit
                ? props.homePageOffer?.second_order_benefit
                : `A free order voucher has been added to your account, and will
                automatically apply to your second order`}
            </div>
          ) : (
            ''
          )}

          {discount > 0 && (
            <div className="cost-item">
              <div className="lt txt-green txt-bold">Discount</div>
              <div className="rt">-${discount}</div>
            </div>
          )}
          <div className="cost-item">
            <div className="lt">Shipping</div>
            <div className="rt">
              {calculatedShippingCharge - Number(ruralDelivery || '') <= 0
                ? 'FREE'
                : '$' +
                  (
                    calculatedShippingCharge - Number(ruralDelivery || '')
                  ).toFixed(2)}
            </div>
          </div>
          {Number(ruralDelivery || '') > 0 && (
            <div className="cost-item">
              <div className="lt">Rural delivery</div>
              <div className="rt">${ruralDelivery}</div>
            </div>
          )}
          <div className="cost-item">
            <div className="lt txt-bold">Total</div>
            <div className="rt">${getTotal().toFixed(2)}</div>
          </div>
        </div>
        {showPayNow && (
          <>
            {!isTester && !oneOff && (
              <div className="text-center mgn-top-20">
                Pause, reschedule or cancel at anytime
              </div>
            )}
            {!isTester &&
              oneOff &&
              oneOffFrequency &&
              showOneOffFrequencyPicker && (
                <div className="text-center mgn-top-20">
                  {'This order will repeat every ' +
                    oneOffFrequency +
                    ' days. Change or cancel any time.'}
                </div>
              )}
            {error.length > 0 ? (
              <div className="bottom-errors">
                {error.map((err, index) => (
                  <div key={index} className="error">
                    {err}
                  </div>
                ))}
              </div>
            ) : (
              <></>
            )}
            <div className="text-center">
              <ButtonLoader
                className={
                  (showLogin && !isLoggedIn()) || lines.length < 1
                    ? 'pay-now-disabled btn-alt pay-now-btn mgn-top-30 width-100-md'
                    : 'btn-alt pay-now-btn mgn-top-30 width-100-md'
                }
                type="submit"
                loading={loading}
                text="Pay Now"
                disabled={(showLogin && !isLoggedIn()) || lines.length < 1}
              />
            </div>
            {/* TODO: Post launch */}
            {/* <div className="text-center mgn-top-20">
              <small>
                By clicking “Pay now”, you are agree to our{' '}
                <a className="txt-underline" href="/">
                  Terms & Conditions
                </a>
              </small>
            </div> */}
          </>
        )}
      </div>
    </div>
  );
};

interface SubscriptionSelectionProps {
  basketId: number;
  frequency?: number;
  setFrequency: Function;
  showFrequencyPicker: boolean;
  setShowFrequencyPicker: Function;
}
const SubscriptionSelection: React.FC<SubscriptionSelectionProps> = props => {
  const {
    basketId,
    frequency,
    setFrequency,
    showFrequencyPicker,
    setShowFrequencyPicker,
  } = props;
  const MIN_FREQUENCY = 2;
  const MAX_FREQUENCY = 180;
  const dispatch = useAppDispatch();

  const onBlur = async event => {
    let value = event?.target?.value;
    if (isNaN(Number(value)) || value < MIN_FREQUENCY) {
      value = MIN_FREQUENCY;
    } else if (value > MAX_FREQUENCY) {
      value = MAX_FREQUENCY;
    }
    setFrequency(value);
    const payload: ToggleOneOffSubscriptionPayload = {
      basketId: basketId,
      isSubscribe: true,
      frequency: value,
    };
    dispatch(toggleOneOffSubscription(payload));
  };

  const handleSwitchUpdate = async () => {
    const payload: ToggleOneOffSubscriptionPayload = {
      basketId: basketId,
      isSubscribe: false,
    };
    if (!showFrequencyPicker) {
      payload.isSubscribe = true;
      payload.frequency = frequency;
      setFrequency(frequency);
    }
    dispatch(toggleOneOffSubscription(payload));
    setShowFrequencyPicker(!showFrequencyPicker);
  };

  const renderFrequencySelector = useCallback(
    () =>
      showFrequencyPicker && (
        <Form.Row>
          <Form.Group as={Col} controlId="formGridPassword">
            <Form.Label>Every&nbsp;</Form.Label>
            <input
              type="number"
              onBlur={onBlur}
              onChange={e => setFrequency(e.target.value)}
              min={2}
              max={180}
              value={frequency}
              className="frequency-input"
            ></input>
            <Form.Label>&nbsp;days</Form.Label>
          </Form.Group>
        </Form.Row>
      ),
    [showFrequencyPicker, frequency],
  );

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <Switch
          left="One off&nbsp;"
          right="Repeat"
          onChange={() => {
            handleSwitchUpdate();
          }}
          switchState={
            showFrequencyPicker ? SwitchState.RIGHT : SwitchState.LEFT
          }
        />
        {renderFrequencySelector()}
      </div>
    </>
  );
};

export default OrderSummary;
